import React from 'react'
import { leagueIds } from 'src/constants'
import { networkLogos } from 'src/constants/networkLogos'
import { isUSFreeTrial } from 'src/modules/utils/constants'

export const xfinityData = {
  competitor: 'Xfinity',
  competitorColor: '#0078D7',
  heroData: {
    title: 'Looking for the best streaming service?',
    subtitle:
      'Compare Fubo streaming service and Xfinity cable TV service on price, features, channels and add-ons to see which is better for you.',
  },
  generalCompareData: {
    heading: 'Compare Fubo vs. Xfinity TV',
    subheading:
      "Here's a general look at both to help you determine which is the best option for live TV entertainment.",
    listItems: ['Channel Count', 'Cloud DVR', 'Simultaneous Streams', 'Starting Price'],
    fuboList: ['200+', 'Unlimited hours', 'Up to 10', '$73.99/mo.*'],
    competitorList: ['125+', '500 GB', 'Up to 5', '$113.30/mo.*'],
    note: '*Prices may vary with taxes and fees. Prices subject to change. Xfinity pricing includes additional fees: $28.00/mo broadcast TV fee and $15.30/mo regional sports fee.',
  },
  prosConsData: {
    heading: 'Pros and Cons: Fubo vs. Xfinity TV',
    content: (
      <>
        <p>
          When it comes to watching live TV and sports, Fubo and Xfinity TV offer two very different
          approaches. Fubo is a modern, sports-first streaming service designed for flexibility and
          affordability, while Xfinity TV is a traditional cable service with a wide range of
          channels but higher costs and additional fees.
        </p>
        <p>
          Fubo is a premium streaming product with a robust channel lineup of 200+ live channels,
          including 50+ sports networks and extensive local sports coverage. It offers unlimited DVR
          storage, up to 10 simultaneous streams, and advanced features like 4K HDR streaming and
          Multiview, which lets you watch up to 4 channels at once on supported devices. Fubo’s
          no-contract model allows users to cancel anytime without penalties, making it a great
          choice for cord cutters.
        </p>
        <p>
          Xfinity Popular TV, on the other hand, provides a more traditional cable experience with
          125+ channels and DVR capabilities. However, it comes with significant drawbacks,
          including additional fees like a $28.00/mo broadcast TV fee and a $15.30/mo regional
          sports fee, which can quickly drive up the cost. Xfinity also requires equipment rentals
          for DVR and lacks the flexibility of streaming from multiple devices without restrictions.
        </p>
      </>
    ),
  },
  plansData: {
    heading: 'Compare Fubo Pro plan vs. Xfinity Popular TV',
    subheading:
      'Let’s take a look at the differences between the popular Fubo Pro plan and Xfinity Popular TV.',
    tableData: {
      Plan: ['Pro plan', 'Popular TV'],
      'Live Channels': ['200+', '125+'],
      'Monthly Price': ['$73.99', '$113.30'],
      'Broadcast TV Fee': ['$0', '$28.00'],
      'Regional Sports Fee': ['Varies by market', '$15.30'],
      'Hours of DVR storage': ['Unlimited', '500 GB'],
      'Free Trial': [isUSFreeTrial ? 'Yes' : 'No', 'No'],
      'Multiple active streams': ['Up to 10', 'Up to 5'],
      '4K HDR streaming': [true, true],
      'No contract required': [true, false],
    },
    note: '*Prices may vary with taxes and fees. Prices subject to change. Xfinity pricing includes additional fees: $28.00/mo broadcast TV fee and $15.30/mo regional sports fee.',
  },
  channelsData: {
    heading: 'Compare Fubo Channels vs. Xfinity Popular TV Channels',
    subheading:
      "This channel comparison chart focuses on what's available among top-watched channels in the Fubo Pro plan vs. Xfinity Popular TV.",
    networksData: [
      { network: networkLogos.espn, fubo: true, competitor: true },
      { network: networkLogos.espn2, fubo: true, competitor: true },
      { network: networkLogos.fs1, fubo: true, competitor: true },
      { network: networkLogos.abc, fubo: true, competitor: true },
      { network: networkLogos.cbs, fubo: true, competitor: true },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.nbc, fubo: true, competitor: true },
      { network: networkLogos.bein, fubo: true, competitor: false },
      { network: networkLogos.nflnetwork, fubo: true, competitor: false },
      { network: networkLogos.mlbnetwork, fubo: true, competitor: false },
      { network: networkLogos.nbatv, fubo: true, competitor: false },
      { network: networkLogos.bigten, fubo: true, competitor: false },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.msnbc, fubo: true, competitor: true },
      { network: networkLogos.disney, fubo: true, competitor: true },
      { network: networkLogos.bravo, fubo: true, competitor: true },
    ],
  },
  completedChannelsData: {
    heading: 'Complete Channel Comparison',
    subheading: 'See how the live channel lineups compare between Fubo Pro and Xfinity Popular TV.',
    content: (
      <>
        <p>
          When it comes to watching live TV and sports, Fubo Pro and Xfinity Popular TV offer very
          different approaches. While both services offer a mix of sports, entertainment, and news
          channels, the differences in their offerings and value propositions are significant.
        </p>
        <p>
          Fubo Pro stands out with its 200+ channels, including 50+ sports networks and exclusive
          channels like NFL Network, Big Ten Network, and beIN Sports. It’s the go-to choice for
          sports fans, offering better coverage of local sports and major leagues. Fubo also
          includes popular entertainment channels like Bravo, Disney Channel, and Nickelodeon,
          making it a great option for families.
        </p>
        <p>
          Xfinity Popular TV, while offering a solid 125+ channels, falls short in sports coverage,
          lacking key networks like NFL Network and Big Ten Network. Additionally, its higher cost
          and additional fees make it less appealing for budget-conscious users. However, it does
          include WarnerMedia networks, which Fubo currently does not offer.
        </p>
      </>
    ),
  },
  faqData: {
    qaData: [
      {
        id: 'fubo-faq-1',
        question: 'What channels does Fubo Pro have over Xfinity Popular TV?',
        answer: (
          <p>
            Fubo Pro includes exclusive sports channels like NFL Network, Big Ten Network, and beIN
            Sports, which are not available on Xfinity Popular TV.
          </p>
        ),
      },
      {
        id: 'fubo-faq-2',
        question: 'Is Fubo Pro better than Xfinity Popular TV for sports?',
        answer: (
          <p>
            Yes, Fubo Pro is better for sports fans with its 50+ sports networks, extensive local
            sports coverage, and exclusive channels like NFL Network and Big Ten Network.
          </p>
        ),
      },
      {
        id: 'fubo-faq-3',
        question: 'How much does Fubo Pro cost vs. Xfinity Popular TV?',
        answer: (
          <p>
            Fubo Pro starts at $73.99/month, while Xfinity Popular TV costs $113.30/month plus
            $43.30 in additional fees for broadcast TV and regional sports.
          </p>
        ),
      },
      {
        id: 'fubo-faq-4',
        question: 'Does Fubo include local channels?',
        answer: (
          <p>Yes, Fubo Pro includes local ABC, CBS, FOX channels in most markets.</p>
          // <p>Yes, Fubo Pro includes local ABC, CBS, FOX, and NBC channels in most markets.</p>
          // TEMPORARY NBC DISPUTE
        ),
      },
      {
        id: 'fubo-faq-5',
        question: 'Can I cancel Fubo anytime?',
        answer: (
          <p>
            Yes, Fubo Pro offers a no-contract subscription, allowing you to cancel anytime without
            penalties.
          </p>
        ),
      },
    ],
  },
  // Show multiple sports (NFL, NBA, NHL, College Basketball, College Football)
  // Only show games on ESPN, ABC, SEC Network, ACC Network (the Disney networks YouTube TV is missing)
  matchtickerSportId: null, // Not needed when using multiple leagues
  matchtickerLeagueName: 'Multiple Sports',
  matchtickerLeagueId: [
    leagueIds.NFL,
    leagueIds.NBA,
    leagueIds.NHL,
    leagueIds["Men's NCAA Basketball"],
    leagueIds['NCAA Football'],
  ],
}
